import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';
const PrivacyConsent = ({ onConsentChange, error }) => {
  const [consents, setConsents] = useState({
    terms: false,
    privacy: false,
    aiProcessing: false,
    marketing: false,
  });

  const handleConsentChange = (field, checked) => {
    const newConsents = { ...consents, [field]: checked };
    setConsents(newConsents);

    const isValid = newConsents?.terms && newConsents?.privacy;
    onConsentChange(newConsents, isValid);
  };

  const consentItems = [
    {
      id: 'terms',
      required: true,
      label: 'I agree to the Terms of Service',
      description: 'Required to create your Cogito account',
      link: '/terms',
      linkText: 'Read Terms',
    },
    {
      id: 'privacy',
      required: true,
      label: 'I accept the Privacy Policy',
      description: 'Learn how we protect and use your data',
      link: '/privacy',
      linkText: 'Read Policy',
    },
    {
      id: 'aiProcessing',
      required: false,
      label: 'Enable AI-powered learning features',
      description: 'Allow AI to analyze your learning patterns for personalized insights (optional)',
      link: '/ai-privacy',
      linkText: 'AI Privacy Details',
    },
    {
      id: 'marketing',
      required: false,
      label: 'Receive learning tips and updates',
      description: 'Get helpful emails about new features and learning strategies (optional)',
      link: null,
      linkText: null,
    },
  ];

  return (
    <div className="space-y-6">
      <div className="bg-accent/10 rounded-lg p-4 space-y-3">
        <div className="flex items-center space-x-2">
          <Icon name="Shield" size={20} color="var(--color-accent)" />
          <h3 className="text-sm font-semibold text-foreground">Privacy-First Learning</h3>
        </div>
        <div className="text-sm text-muted-foreground space-y-2">
          <p>Cogito is designed with your privacy in mind:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Your learning data stays under your control</li>
            <li>Optional local AI processing (no cloud required)</li>
            <li>Export your data anytime</li>
            <li>No tracking or selling of personal information</li>
          </ul>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-foreground">Account Permissions</h3>

        {consentItems.map((item) => (
          <div key={item.id} className="space-y-2">
            <div className="flex items-start space-x-3">
              <Checkbox
                checked={consents?.[item.id]}
                onChange={(e) => handleConsentChange(item.id, e?.target?.checked)}
                className="mt-0.5"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2">
                  <label className="text-sm font-medium text-foreground cursor-pointer">
                    {item.label}
                  </label>
                  {item.required && <span className="text-xs text-error font-medium">Required</span>}
                </div>
                <p className="text-xs text-muted-foreground mt-1">{item.description}</p>
                {item.link && (
                  <button
                    type="button"
                    onClick={() => window.open(item.link, '_blank')}
                    className="text-xs text-primary hover:text-primary/80 transition-colors mt-1 flex items-center space-x-1"
                  >
                    <span>{item.linkText}</span>
                    <Icon name="ExternalLink" size={12} />
                  </button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {consents?.aiProcessing && (
        <div className="bg-primary/10 rounded-lg p-4 space-y-3">
          <div className="flex items-center space-x-2">
            <Icon name="Cpu" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">AI Processing Options</span>
          </div>
          <div className="text-xs text-muted-foreground space-y-2">
            <p>You can choose how AI features work:</p>
            <ul className="list-disc list-inside space-y-1 ml-2">
              <li><strong>Cloud AI:</strong> Faster processing, requires internet</li>
              <li><strong>Local AI:</strong> Private processing on your device</li>
              <li><strong>Hybrid:</strong> Best of both based on your preference</li>
            </ul>
            <p className="text-primary">You can change this anytime in Settings.</p>
          </div>
        </div>
      )}

      {error && (
        <div className="flex items-center space-x-2 text-error text-sm">
          <Icon name="AlertCircle" size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="text-xs text-muted-foreground bg-muted/30 rounded-lg p-3">
        <div className="flex items-start space-x-2">
          <Icon name="Info" size={14} className="mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium text-foreground mb-1">Your Data, Your Control</p>
            <p>
              All your learning data belongs to you. Cogito provides tools to export, backup,
              and delete your information at any time. We believe in transparent data practices
              and will never sell your personal information.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyConsent;